import {
  type ArtefactKind,
  ARTEFACT_KINDS,
  artefactKindForMediaType,
  mediaTypeForFilename,
} from '@zevik/schemas';

/**
 * What a chosen file will be uploaded as, decided before any request
 * (docs/DECISIONS.md ZD-124 §3).
 *
 * ## The store checks; this suggests
 *
 * `POST /:id/artefacts` takes a declared `kind` and refuses one the media
 * type contradicts — a check and not an inference, in the store's own words.
 * A client cannot be asked what `structured` means, so the page reads the
 * kind the file's type admits off the same table the store checks against
 * (`ARTEFACT_MEDIA_TYPES`, in `@zevik/schemas` so that it is one table) and
 * puts it in front of the person as a pre-selection they confirm or change.
 * The check then has something to bite on: a person who changes it is
 * stating a kind, and the store may still refuse.
 *
 * ## The media type is the browser's, then the file name's, then nobody's
 *
 * `File.type` is what the platform maps the extension to and is empty for
 * `.md` and `.yaml` on at least one platform. Where it is empty the type is
 * read off the extension — a claim about the file's *name*, made before any
 * request and consulting nothing the record knows, which is the line ZD-118
 * draws for what a page may decide alone. Where neither says anything the
 * file is refused here, with what to do, rather than sent as
 * `application/octet-stream` for the store to refuse with less to say.
 */

export interface UploadPlan {
  media_type: string;
  /** The kind the media type admits, or `null` where the type is one no kind takes. */
  kind: ArtefactKind | null;
}

export class UnreadableFileTypeError extends Error {
  constructor(filename: string) {
    super(
      `The type of “${filename}” could not be read from the file or its name. Rename it with ` +
        `an extension the console knows — .pdf, .md, .txt, .json, .yaml, .png, .jpg, .svg — ` +
        `and choose it again.`,
    );
    this.name = 'UnreadableFileTypeError';
  }
}

export function planUpload(file: { name: string; type: string }): UploadPlan {
  const mediaType = file.type.length > 0 ? file.type : mediaTypeForFilename(file.name);
  if (mediaType === null) throw new UnreadableFileTypeError(file.name);
  return { media_type: mediaType, kind: artefactKindForMediaType(mediaType) };
}

/** The kinds a person may state, in the schema's order, with the words a person reads. */
export const KIND_LABELS: Readonly<Record<ArtefactKind, string>> = {
  diagram_image: 'Architecture diagram (image)',
  pdf: 'PDF document',
  text: 'Text or Markdown document',
  structured: 'Structured description (JSON or YAML)',
};

export const KINDS: readonly ArtefactKind[] = ARTEFACT_KINDS;
